/**
 * useRateAssignments - Hook for managing rate assignments
 * Provides CRUD operations for time-bounded rate assignments
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import type {
  RateAssignmentRecord,
  RateAssignmentFormData,
  RateAssignmentWithDetails,
  BulkRateAssignmentRequest,
  BulkRateAssignmentResponse,
} from '@/types/rates';
import { authenticatedFetch } from '@/lib/api';

const API_BASE = '/api/v1/admin/rate-assignments';

interface UseRateAssignmentsOptions {
  /** Filter by entity type */
  entityType?: 'company' | 'group' | 'unit';
  /** Filter by entity ID */
  entityId?: string;
  /** Filter by rate ID */
  rateId?: string;
  /** Include expired assignments */
  includeExpired?: boolean;
  /** Auto-fetch on mount */
  autoFetch?: boolean;
}

interface UseRateAssignmentsReturn {
  // Data
  assignments: RateAssignmentWithDetails[];
  totalCount: number;

  // Loading states
  isLoading: boolean;
  isSubmitting: boolean;

  // Error state
  error: string | null;

  // Actions
  fetchAssignments: () => Promise<void>;
  fetchEntityAssignments: (
    entityType: 'company' | 'group' | 'unit',
    entityId: string
  ) => Promise<RateAssignmentWithDetails[]>;
  createAssignment: (data: RateAssignmentFormData) => Promise<RateAssignmentRecord | null>;
  updateAssignment: (
    id: string,
    data: Partial<RateAssignmentFormData>
  ) => Promise<RateAssignmentRecord | null>;
  deleteAssignment: (id: string) => Promise<boolean>;
  bulkCreateAssignments: (
    data: BulkRateAssignmentRequest
  ) => Promise<BulkRateAssignmentResponse | null>;
  refresh: () => Promise<void>;
}

export function useRateAssignments(
  options: UseRateAssignmentsOptions = {}
): UseRateAssignmentsReturn {
  const {
    entityType,
    entityId,
    rateId,
    includeExpired = false,
    autoFetch = true,
  } = options;

  const [assignments, setAssignments] = useState<RateAssignmentWithDetails[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAssignments = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (entityType) params.set('entityType', entityType);
      if (entityId) params.set('entityId', entityId);
      if (rateId) params.set('rateId', rateId);
      if (includeExpired) params.set('includeExpired', 'true');

      const response = await authenticatedFetch(`${API_BASE}?${params.toString()}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setAssignments(data.assignments || []);
      setTotalCount(data.total || 0);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch assignments';
      setError(message);
      console.error('[useRateAssignments] fetchAssignments error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [entityType, entityId, rateId, includeExpired]);

  const fetchEntityAssignments = useCallback(
    async (
      targetEntityType: 'company' | 'group' | 'unit',
      targetEntityId: string
    ): Promise<RateAssignmentWithDetails[]> => {
      try {
        const params = new URLSearchParams();
        if (includeExpired) params.set('includeExpired', 'true');

        const response = await authenticatedFetch(
          `${API_BASE}/entity/${targetEntityType}/${targetEntityId}?${params.toString()}`
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}`);
        }

        const data = await response.json();
        return data.assignments || [];
      } catch (err) {
        console.error('[useRateAssignments] fetchEntityAssignments error:', err);
        return [];
      }
    },
    [includeExpired]
  );

  const createAssignment = useCallback(
    async (data: RateAssignmentFormData): Promise<RateAssignmentRecord | null> => {
      setIsSubmitting(true);
      setError(null);

      try {
        const response = await authenticatedFetch(API_BASE, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}`);
        }

        const result = await response.json();
        toast.success('Rate assignment created');

        // Refresh the list
        await fetchAssignments();

        return result.assignment;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create assignment';
        setError(message);
        toast.error(message);
        console.error('[useRateAssignments] createAssignment error:', err);
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [fetchAssignments]
  );

  const updateAssignment = useCallback(
    async (
      id: string,
      data: Partial<RateAssignmentFormData>
    ): Promise<RateAssignmentRecord | null> => {
      setIsSubmitting(true);
      setError(null);

      try {
        const response = await authenticatedFetch(`${API_BASE}/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}`);
        }

        const result = await response.json();
        toast.success('Rate assignment updated');

        // Update local state
        setAssignments(prev =>
          prev.map(a => (a.id === id ? { ...a, ...result.assignment } : a))
        );

        return result.assignment;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update assignment';
        setError(message);
        toast.error(message);
        console.error('[useRateAssignments] updateAssignment error:', err);
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    []
  );

  const deleteAssignment = useCallback(
    async (id: string): Promise<boolean> => {
      setIsSubmitting(true);
      setError(null);

      try {
        const response = await authenticatedFetch(`${API_BASE}/${id}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}`);
        }

        toast.success('Rate assignment deleted');

        // Refresh the list
        await fetchAssignments();

        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete assignment';
        setError(message);
        toast.error(message);
        console.error('[useRateAssignments] deleteAssignment error:', err);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [fetchAssignments]
  );

  const bulkCreateAssignments = useCallback(
    async (data: BulkRateAssignmentRequest): Promise<BulkRateAssignmentResponse | null> => {
      setIsSubmitting(true);
      setError(null);

      try {
        const response = await authenticatedFetch(`${API_BASE}/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}`);
        }

        const result: BulkRateAssignmentResponse = await response.json();

        if (result.created > 0) {
          toast.success(
            `Created ${result.created} assignment${result.created !== 1 ? 's' : ''}`
          );
        }
        if (result.skipped > 0) {
          toast.warning(
            `Skipped ${result.skipped} (overlapping assignments)`
          );
        }
        if (result.errors.length > 0) {
          toast.error(`${result.errors.length} error${result.errors.length !== 1 ? 's' : ''}`);
        }

        // Refresh the list
        await fetchAssignments();

        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create bulk assignments';
        setError(message);
        toast.error(message);
        console.error('[useRateAssignments] bulkCreateAssignments error:', err);
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [fetchAssignments]
  );

  const refresh = useCallback(async () => {
    await fetchAssignments();
  }, [fetchAssignments]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchAssignments();
    }
  }, [autoFetch]);

  return {
    assignments,
    totalCount,
    isLoading,
    isSubmitting,
    error,
    fetchAssignments,
    fetchEntityAssignments,
    createAssignment,
    updateAssignment,
    deleteAssignment,
    bulkCreateAssignments,
    refresh,
  };
}
